import { useState } from 'react';

import useLocale from '@/hooks/useLocale';
import useFeature from '@/hooks/useFeature';
import useAuth from '@/context/AuthContext';
import { getLocaleInfo } from '@/utils/helpers';

import { cart } from '@/lib/cart';
import { ShopifyCart, ShopifyCartItem } from '@/lib/shopify';

import { buildSubscriptionPlanKey } from './payloadBuilders/buildSubscriptionPlanKey';
import { buildPlayKitSubscriptionKey } from './payloadBuilders/buildPlayKitSubscriptionKey';
import { buildGiftSubscriptionKey } from './payloadBuilders/buildGiftSubscriptionKey';

import { redirectToPlayGeniusCheckout } from './utils/redirectToPlayGeniusCheckout';
import { startPlayGeniusCheckout } from './utils/startPlayGeniusCheckout';
import { organizeCartItems } from './utils/cartHelpers';

import {
  GiftSubscriptionSettings,
  PlayKitSubscriptionSettings,
  SubscriptionPlanSettings,
} from './payloadBuilders/types';

export interface StartPlayGeniusCheckoutPayload extends ShopifyCart {
  user_uuid?: string;
  gifts?: GiftSubscriptionSettings;
  bundle_products?: ShopifyCartItem[];
  subscription_plans?: SubscriptionPlanSettings[];
  subscriptions?: [PlayKitSubscriptionSettings];
}

export enum PlayGeniusCheckoutSource {
  PlayKitSubscriptionFlow = 'subflow',
  GiftSubscriptionFlow = 'gift-subflow',
  ProductPage = 'pdp',
  Cart = 'cart',
}

export function usePlayGeniusCheckout(source: PlayGeniusCheckoutSource) {
  const [errorOccurred, setErrorOccurred] = useState<boolean>(false);

  const locale = useLocale();
  const localeInfo = getLocaleInfo(locale);

  const isCheckoutRewriteEnabled =
    useFeature('growth__enable-checkout-rewrite') === true;

  const flags = { 'growth__enable-checkout-rewrite': isCheckoutRewriteEnabled };

  const { user } = useAuth();
  const userUuid = user?.attributes.loveveryId;

  async function continueToPlayGeniusCheckout() {
    if (typeof window === 'undefined') return;

    // reset error state
    if (errorOccurred) {
      setErrorOccurred(false);
    }

    const shopifyCartData: ShopifyCart = await cart.get();
    const originalCartItems = shopifyCartData.items;
    const emptyCart: ShopifyCartItem[] = [];

    const payload: StartPlayGeniusCheckoutPayload = {
      ...shopifyCartData,
      items: emptyCart,
      user_uuid: userUuid,
    };

    let isRecurringPuf = false;

    const {
      coursePackProducts,
      playKitGiftSubscriptionProduct,
      playKitSubscriptionProduct,
      playKitSubscriptionBookBundleAddonProduct,
      subscriptionPlanProducts,
      standaloneProducts,
    } = organizeCartItems(originalCartItems);

    // Diverted Code Path: Play Kit Gift Subscription Checkouts short circuit.
    // This means if this product is in the cart, all other cart items are ignored,
    // and we build a custom tailored payload, only including the gift subscription product.
    if (playKitGiftSubscriptionProduct) {
      const giftSubscriptionSettings = buildGiftSubscriptionKey(
        playKitGiftSubscriptionProduct,
      );

      if (!giftSubscriptionSettings) {
        // If the Play Kit Gift Subscription Product was not added to the cart with expected cart line item properties, we cannot proceed.
        // Invoker can determine how to handle this error.
        setErrorOccurred(true);
      } else {
        // Otherwise, we can proceed with api interaction to start the checkout process.
        const giftSubscriptionCheckoutPayload = {
          ...payload,
          gifts: giftSubscriptionSettings,
          items: [
            {
              handle: 'subscription-gifting',
            },
          ] as unknown as ShopifyCartItem[],
        };

        try {
          const response = await startPlayGeniusCheckout({
            payload: giftSubscriptionCheckoutPayload,
            flags,
            source,
          });

          await redirectToPlayGeniusCheckout({
            invoiceId: response.uuid,
            isRecurringPuf: false,
            localeInfo,
            shouldRedirectToNewCheckout: isCheckoutRewriteEnabled,
            source,
          });
        } catch (error) {
          // If the API returns an error, we can not proceed with redirecting the user to PlayGenius checkout.
          // Invoker can determine how to handle this error.
          setErrorOccurred(true);
        }
      }

      return;
    }

    // Primary Code Path: all other carts.
    payload.items = standaloneProducts;
    payload.bundle_products = coursePackProducts;

    if (playKitSubscriptionProduct) {
      const playKitSubscriptionSettings = buildPlayKitSubscriptionKey(
        playKitSubscriptionProduct,
        playKitSubscriptionBookBundleAddonProduct,
      );

      if (playKitSubscriptionSettings) {
        payload.subscriptions = [playKitSubscriptionSettings];

        isRecurringPuf = false;
      }
    }

    if (subscriptionPlanProducts.length) {
      const subscriptionPlanSettings = buildSubscriptionPlanKey(
        subscriptionPlanProducts,
      );

      if (subscriptionPlanSettings) {
        payload.subscription_plans = subscriptionPlanSettings;
      }
    }

    try {
      const response = await startPlayGeniusCheckout({
        payload,
        flags,
        source,
      });

      await redirectToPlayGeniusCheckout({
        invoiceId: response.uuid,
        localeInfo,
        source,
        isRecurringPuf,
        shouldRedirectToNewCheckout: isCheckoutRewriteEnabled,
      });
    } catch (error) {
      // use case: we ran into an error redirecting to PG checkout.
      // Invoker can determine how to handle this error.
      setErrorOccurred(true);
    }
  }

  return {
    continueToPlayGeniusCheckout,
    errorOccurred,
  };
}
